import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Banknote,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  DoorOpen,
  Inbox,
  Info,
  MessageSquare,
  UserRound,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { AperturaDeCaja, EstadoDeLaCaja } from '@/shared/api/tipos';
import { Modal } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';

import {
  BILLETES,
  MONEDAS,
  type Conteo,
  type Pieza,
  importeDe,
  loContado,
  subtotalDe,
  totalContado,
} from './piezas';
import { enFecha } from './fechas';

export function ModalDeApertura({
  estado,
  alAbierta,
  alCancelar,
}: {
  estado: EstadoDeLaCaja;
  alAbierta: (apertura: AperturaDeCaja) => void;
  alCancelar: () => void;
}) {
  const [conteo, setConteo] = useState<Conteo>({});
  const [motivo, setMotivo] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<string | null>(null);

  const clienteDeConsultas = useQueryClient();

  const contado = useMemo(() => totalContado(conteo), [conteo]);

  const e = estado;
  const esperado = e.fondoEsperado;
  const diferencia = Math.round((contado - esperado) * 100) / 100;
  const seComprueba = esperado > 0;

  const abrir = useMutation({
    mutationFn: () =>
      api.post<AperturaDeCaja>('/aperturas', {
        conteo: loContado(conteo),
        motivoDiferencia: motivo.trim() || undefined,
        observaciones: observaciones.trim() || undefined,
      }),
    onSuccess: (apertura) => {
      notificar.exito('Caja abierta');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['caja-estado'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['cierre-previa'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['aperturas'] });
      alAbierta(apertura);
    },
    onError: (causa) => {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo abrir la caja';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const faltaMotivo = seComprueba && diferencia !== 0 && motivo.trim().length < 10;

  function contar(pieza: Pieza, piezas: string) {
    setConteo({ ...conteo, [pieza.clave]: piezas });
    setError(null);
  }

  return (
    <Modal
      abierto
      alCerrar={alCancelar}
      titulo="Apertura de caja"
      subtitulo={`${e.nombreDelCajero}${e.caja ? ` · Caja ${e.caja}` : ''}`}
      icono={DoorOpen}
      ancho="pantalla"

      cerrarAlPulsarFuera={false}
    >
      <div className="grid items-start gap-5 xl:grid-cols-[19rem_1fr_17rem]">

        <div className="space-y-4">
          <div className="relative overflow-hidden rounded-2xl bg-armazon-900 p-5 text-white shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-white/60">
              Debe haber al abrir
            </p>
            <p className="importe mt-1 text-3xl font-bold">${esperado.toFixed(2)}</p>
            <p className="mt-1 text-[11px] uppercase text-white/60">
              {seComprueba ? 'Fondo de apertura requerido' : 'Sin fondo configurado'}
            </p>
            <Inbox className="absolute -bottom-2 right-3 size-16 text-white/10" aria-hidden />
          </div>

          <div className="rounded-2xl border border-armazon-200 bg-white p-5">
            <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
              <CalendarDays className="size-3.5" aria-hidden />
              Turno de la jornada
            </p>
            <p className="importe mt-2 flex items-baseline justify-between gap-3">
              <span className="text-xs uppercase text-armazon-600">Efectivo</span>
              <span className="text-lg font-bold text-marca-700">${contado.toFixed(2)}</span>
            </p>
            <p className="mt-3 text-[11px] uppercase text-armazon-500">
              Punto de caja: {e.caja ? `Caja ${e.caja}` : 'sin caja asignada'}
            </p>
          </div>

          <div className="rounded-2xl border border-armazon-200 bg-armazon-50/50 p-5">
            <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
              <UserRound className="size-3.5" aria-hidden />
              Usuario · caja · fecha
            </p>
            <dl className="mt-3 space-y-1.5 text-[11px] uppercase">
              <Fila rotulo="Usuario" valor={e.nombreDelCajero} />
              <Fila rotulo="Caja" valor={e.caja ? `Caja ${e.caja}` : '—'} />
              <Fila rotulo="Fecha" valor={enFecha(e.fecha)} />
            </dl>
            <p className="mt-3 text-justify text-[10px] uppercase leading-relaxed text-armazon-500">
              Verifique el fondo antes de abrir: una vez registrado no se puede cambiar.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-armazon-200 bg-white p-5 shadow-sm">
            <p className="flex items-center gap-2 border-b border-armazon-100 pb-3 text-sm font-bold uppercase text-armazon-800">
              <Banknote className="size-4 text-armazon-500" aria-hidden />
              Registre el fondo inicial
            </p>

            <div className="grid gap-5 pt-4 md:grid-cols-2">
              <ColumnaDePiezas
                titulo="Billetes"
                subtitulo="billete"
                piezas={BILLETES}
                conteo={conteo}
                alContar={contar}
                subtotal={subtotalDe(conteo, BILLETES)}
              />
              <div className="md:border-l md:border-armazon-100 md:pl-5">
                <ColumnaDePiezas
                  titulo="Monedas"
                  subtitulo="moneda"
                  piezas={MONEDAS}
                  conteo={conteo}
                  alContar={contar}
                  subtotal={subtotalDe(conteo, MONEDAS)}
                />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between rounded-xl bg-armazon-50 px-4 py-3">
              <span className="text-[11px] font-bold uppercase tracking-wide text-armazon-700">
                Total contado
              </span>
              <span className="importe text-lg font-bold text-armazon-900">
                ${contado.toFixed(2)}
              </span>
            </div>
          </div>

          {seComprueba && diferencia !== 0 && (
            <label className="block rounded-2xl border border-armazon-200 bg-white p-4">
              <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-armazon-700">
                <AlertCircle className="size-3.5" aria-hidden />
                Por qué no cuadra <span className="text-alerta">*</span>
              </span>
              <textarea
                value={motivo}
                onChange={(evento) => {
                  setMotivo(evento.target.value);
                  setError(null);
                }}
                rows={2}
                maxLength={500}
                placeholder="Tesorería entregó $95 en vez de $100."
                className="mt-2 w-full rounded-lg border border-armazon-200 px-3 py-2 text-sm outline-none transition-colors focus:border-marca-400"
              />
              <span className="mt-1.5 block text-justify text-[11px] leading-snug text-armazon-500">
                Se puede abrir sin cuadrar, pero no en silencio. Un faltante a esta hora todavía se
                puede resolver.
              </span>
            </label>
          )}

          <label className="block rounded-2xl border border-armazon-200 bg-white p-4">
            <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-armazon-700">
              <MessageSquare className="size-3.5" aria-hidden />
              Observaciones
            </span>
            <textarea
              value={observaciones}
              onChange={(evento) => setObservaciones(evento.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Turno de la mañana. Fondo recibido de tesorería."
              className="mt-2 w-full rounded-lg border border-armazon-200 px-3 py-2 text-sm outline-none transition-colors focus:border-marca-400"
            />
          </label>
        </div>

        <div className="rounded-2xl border border-armazon-200 bg-white p-5 shadow-sm">
          <p className="flex items-center gap-2 border-b border-armazon-100 pb-3 text-[11px] font-bold uppercase tracking-wide text-armazon-700">
            <ClipboardList className="size-3.5" aria-hidden />
            Resumen de apertura
          </p>

          <dl className="mt-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-[11px] font-semibold uppercase text-armazon-600">Contado</dt>
              <dd className="importe text-lg font-bold text-armazon-900">${contado.toFixed(2)}</dd>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-dashed border-armazon-200 pt-3">
              <dt className="text-[11px] font-semibold uppercase text-armazon-600">
                Fondo requerido
              </dt>
              <dd className="importe text-lg font-bold text-armazon-900">${esperado.toFixed(2)}</dd>
            </div>
          </dl>

          <div className="mt-4">
            {seComprueba ? (
              <Diferencia diferencia={diferencia} />
            ) : (
              <p className="rounded-xl border border-armazon-200 bg-armazon-50 p-4 text-justify text-[11px] uppercase leading-relaxed text-armazon-600">
                No hay fondo configurado, así que no hay nada que cuadrar: se registra lo que
                cuente.
              </p>
            )}
          </div>
        </div>
      </div>

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-armazon-100 pt-4">
        <p className="flex min-w-0 flex-1 items-center gap-3 text-justify text-[11px] uppercase leading-relaxed text-armazon-500">
          <Info className="size-5 shrink-0 text-blue-600" aria-hidden />
          <span>
            Al abrir queda registrado el fondo y podrá cobrar. Ese importe se descontará al cerrar y{' '}
            <b>no se puede cambiar después</b>.
          </span>
        </p>

        <button
          type="button"
          onClick={() => {
            setError(null);
            abrir.mutate();
          }}
          disabled={faltaMotivo || abrir.isPending}
          className="flex h-12 shrink-0 items-center justify-center gap-3 rounded-xl bg-marca-600 px-8 text-sm font-bold uppercase tracking-wide text-white shadow-sm transition-colors hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-55"
        >
          <DoorOpen className="size-5" aria-hidden />
          {abrir.isPending ? 'Abriendo…' : 'Abrir la caja'}
        </button>
      </div>
    </Modal>
  );
}

function Fila({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-armazon-500">{rotulo}</dt>
      <dd className="min-w-0 truncate font-semibold text-armazon-800">{valor}</dd>
    </div>
  );
}

function ColumnaDePiezas({
  titulo,
  subtitulo,
  piezas,
  conteo,
  alContar,
  subtotal,
}: {
  titulo: string;
  subtitulo: string;
  piezas: readonly Pieza[];
  conteo: Conteo;
  alContar: (pieza: Pieza, piezas: string) => void;
  subtotal: number;
}) {
  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between gap-3 text-[10px] font-semibold uppercase tracking-wide text-armazon-400">
        <span>{titulo}</span>
        <span>Cantidad</span>
      </div>

      <div className="space-y-2.5">
        {piezas.map((pieza) => (
          <label key={pieza.clave} className="flex items-center gap-3">
            <span className="w-12 shrink-0">
              <span className="importe block text-sm font-bold text-armazon-800">
                {pieza.etiqueta}
              </span>
              <span className="block text-[9px] uppercase text-armazon-400">{subtitulo}</span>
            </span>
            <input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={conteo[pieza.clave] ?? ''}
              onChange={(evento) => alContar(pieza, evento.target.value)}
              placeholder="0"
              className="importe h-10 w-full min-w-0 flex-1 rounded-lg border border-armazon-200 px-2 text-center text-sm outline-none transition-colors focus:border-marca-400"
            />
            <span className="importe w-16 shrink-0 text-right text-xs text-armazon-500">
              ${importeDe(conteo, pieza).toFixed(2)}
            </span>
          </label>
        ))}
      </div>

      <p className="mt-3 flex items-baseline justify-between gap-3 border-t border-armazon-100 pt-2.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
          Subtotal {titulo.toLowerCase()}
        </span>
        <span className="importe text-sm font-bold text-armazon-800">${subtotal.toFixed(2)}</span>
      </p>
    </div>
  );
}

function Diferencia({ diferencia }: { diferencia: number }) {
  const cuadra = diferencia === 0;
  const sobra = diferencia > 0;

  const marco = cuadra
    ? 'border-marca-200 bg-marca-50'
    : sobra
      ? 'border-blue-200 bg-blue-50'
      : 'border-red-200 bg-red-50';
  const tinta = cuadra ? 'text-marca-800' : sobra ? 'text-blue-800' : 'text-red-800';

  return (
    <div className={`rounded-xl border p-4 text-center ${marco}`}>
      <p
        className={`flex items-center justify-center gap-2 text-[11px] font-bold uppercase ${tinta}`}
      >
        {cuadra ? (
          <CheckCircle2 className="size-4 shrink-0" aria-hidden />
        ) : (
          <AlertCircle className="size-4 shrink-0" aria-hidden />
        )}
        {cuadra ? 'Cuadra perfectamente' : sobra ? 'Sobra en el fondo' : 'Falta en el fondo'}
      </p>
      <p className={`importe mt-1 text-2xl font-bold ${tinta}`}>
        ${Math.abs(diferencia).toFixed(2)}
      </p>
      <p className={`mt-1 text-[10px] uppercase leading-relaxed ${tinta}`}>
        {cuadra
          ? 'Puede abrir la caja e iniciar la jornada.'
          : 'Diga por qué antes de abrir: quedará escrito.'}
      </p>
    </div>
  );
}
